import { Controller } from "stimulus";
import { getMaxSearchRadius, getDistanceUnit } from '../../helpers/radius_search_helpers';
import { getState } from '../../search_results/state';
import mapConverters from '../../map/map_converters';
import mapEvents from '../../map/map_events';

export default class extends Controller {
  static targets = ["addressInput", "radiusInput", "confirmButton"];

  connect() {
    this.selectRadiusSearchButton();
    this.selectedPlace = null;
    this.geocodedCoordinates = null;
    this.autocomplete = null;
    this.selectedAddressText = null;
    this._gmpSelectHandler = null;
    this._addressEnterKeyHandler = null;

    // Reset confirm button state
    this.disableConfirmButton();

    void this.initializeGooglePlaces();
  }

  disconnect() {
    this.deselectRadiusSearchButton();
    if (this.autocomplete && this._gmpSelectHandler) {
      this.autocomplete.removeEventListener('gmp-select', this._gmpSelectHandler);
    }
    if (this.autocomplete && this._addressEnterKeyHandler) {
      this.autocomplete.removeEventListener('keydown', this._addressEnterKeyHandler, true);
    }
    this.autocomplete = null;
    this._gmpSelectHandler = null;
    this._addressEnterKeyHandler = null;
  }

  clearCoordinates() {
    this.selectedPlace = null;
    this.geocodedCoordinates = null;
    this.selectedAddressText = null;
  }

  selectRadiusSearchButton() {
    $('#radiusSearchBtn').addClass('active');
  }

  deselectRadiusSearchButton() {
    $('#radiusSearchBtn').removeClass('active');
  }
  
  radiusIncrease() {
    const newRadius = Number(this.radiusInputTarget.value) + 1;

    if (newRadius <= getMaxSearchRadius()) {
      this.setRadiusInputValue(newRadius);
    } else if (newRadius > getMaxSearchRadius()) {
      this.toastDistanceTooHigh();
      this.setRadiusInputValue(getMaxSearchRadius());
    } else {
      this.setRadiusInputValue(getMaxSearchRadius());
    }
  }

  radiusDecrease() {
    const newRadius = Number(this.radiusInputTarget.value) - 1;

    if (newRadius > 0) {
      this.setRadiusInputValue(newRadius);
    } else if (newRadius <= 0) {
      this.toastDistanceTooLow();
      this.setRadiusInputValue(1);
    } else {
      this.setRadiusInputValue(1);
    }
  }

  // Formats the radius input
  // 2 decimal places if it's not an integer, otherwise return the integer
  formatRadiusInputValue(distance = 1) {
    distance = parseFloat(distance);
    // Default to 0 for invalid input
    if (isNaN(distance)) {
      this.toastInvalidDistance();
      return 1;
    }

    // Check if the distance is within the valid range
    if (distance <= 0) {
      this.toastDistanceTooLow();
      return 1;
    } else if (distance > getMaxSearchRadius()) {
      this.toastDistanceTooHigh();
      return getMaxSearchRadius();
    }
    
    if (Number.isInteger(distance)) {
      return parseInt(distance);
    } else {
      return distance.toFixed(2);
    }
  }

  // This can be called directly (like a blur event) or with a value
  setRadiusInputValue(eventOrValue) {
    const value = eventOrValue instanceof Event ? eventOrValue.target.value : eventOrValue;
    this.radiusInputTarget.value = this.formatRadiusInputValue(value);
  }

  async initializeGooglePlaces() {
    const addressHost = this.addressInputTarget;
    if (!addressHost) {
      console.warn('#radiusSearch_address host not found');
      this.closeModal();
      this.toastRadiusSearchUnavailable();
      return;
    }

    if (!window.google || !window.google.maps) {
      console.error('Google Maps JavaScript API not loaded.');
      this.closeModal();
      this.toastRadiusSearchUnavailable();
      return;
    }

    try {
      if (typeof google.maps.importLibrary === 'function') {
        await google.maps.importLibrary('places');
      }

      const PlaceAutocompleteElement = google.maps.places.PlaceAutocompleteElement;
      if (!PlaceAutocompleteElement) {
        throw new Error('PlaceAutocompleteElement is not available');
      }

      const placeholder = addressHost.dataset.placeholder || '';
      addressHost.innerHTML = '';

      this.autocomplete = new PlaceAutocompleteElement({
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise']
      });
      this.autocomplete.placeholder = placeholder;

      addressHost.appendChild(this.autocomplete);

      const bindAddressFieldAttrs = () => {
        const innerInput = this.autocomplete.input;
        if (innerInput) {
          innerInput.id = 'radiusSearch_address_input';
          innerInput.setAttribute('name', 'radius_search_address');
          innerInput.setAttribute('aria-labelledby', 'radiusSearch_address_label');
          return true;
        }
        return false;
      };
      if (!bindAddressFieldAttrs()) {
        requestAnimationFrame(() => {
          if (!bindAddressFieldAttrs()) {
            this.autocomplete.id = 'radiusSearch_address_input';
            this.autocomplete.setAttribute('name', 'radius_search_address');
          }
        });
      }

      this._gmpSelectHandler = (event) => {
        void this.handleGmpSelect(event);
      };
      this.autocomplete.addEventListener('gmp-select', this._gmpSelectHandler);

      this._addressEnterKeyHandler = (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      };
      this.autocomplete.addEventListener('keydown', this._addressEnterKeyHandler, true);
    } catch (error) {
      console.error('Error initializing Google Places PlaceAutocompleteElement:', error);
      this.closeModal();
      this.toastRadiusSearchUnavailable();
    }
  }

  async handleGmpSelect(event) {
    if (!this.autocomplete) {
      return;
    }

    const placePrediction = event.placePrediction ||
      (event.detail && event.detail.placePrediction);
    if (!placePrediction) {
      this.disableConfirmButton();
      this.clearCoordinates();
      return;
    }

    const place = placePrediction.toPlace();
    try {
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress']
      });
    } catch (fetchError) {
      console.warn('Radius Search: Could not fetch place fields', fetchError);
      this.disableConfirmButton();
      this.clearCoordinates();
      this.toastInvalidAddress();
      return;
    }

    this.selectedAddressText = place.formattedAddress || '';
    if (!this.selectedAddressText) {
      this.disableConfirmButton();
      this.clearCoordinates();
      return;
    }

    this.selectedPlace = place;
    const addressComponents = this.extractAddressComponents(place);

    this.disableConfirmButton();
    this.callGeocoderService(addressComponents);
  }

  extractAddressComponents(place) {
    const components = {};
    const raw = place.addressComponents || place.address_components;
    if (!raw || !raw.length) {
      return components;
    }

    raw.forEach((component) => {
      const types = component.types || [];
      const longName = component.longText != null ? component.longText : component.long_name;
      const shortName = component.shortText != null ? component.shortText : component.short_name;
      if (types.includes('street_number')) {
        components.house_nbr = longName;
      } else if (types.includes('route')) {
        components.route = longName;
      } else if (types.includes('locality')) {
        components.city = longName;
      } else if (types.includes('administrative_area_level_1')) {
        components.state = shortName;
      } else if (types.includes('postal_code')) {
        components.postal_code = longName;
      } else if (types.includes('administrative_area_level_2')) {
        components.county = longName.replace(/ (County|Parish)$/i, '');
      } else if (types.includes('country')) {
        components.country = shortName;
      }
    });

    return components;
  }

  async callGeocoderService(addressComponents) {
    // Filter out blank values from address components
    const filteredComponents = {};
    Object.keys(addressComponents).forEach(key => {
      if (addressComponents[key]) {
        filteredComponents[key] = addressComponents[key];
      }
    });

    // If no address components, cannot geocode
    if (Object.keys(filteredComponents).length === 0) {
      console.warn('Radius Search: No address components to send to geocoder');
      this.clearCoordinates();
      this.toastInvalidAddress();
      return;
    }

    try {
      // Build the Rails route URL
      const portalPrefix = (typeof Flexmls !== 'undefined' && Flexmls.portalSlugPrefix) ? Flexmls.portalSlugPrefix : '';
      const geocodeUrl = `${portalPrefix}/search/geocode`;

      const csrfToken = document.querySelector('[name="csrf-token"]')?.content;

      const response = await fetch(geocodeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'same-origin',
        body: JSON.stringify({address: filteredComponents})
      });
      
      if (!response.ok) {
        throw new Error(`Geocoder service returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data && data.success && data.matches && data.matches.length > 0) {
        const firstMatch = data.matches[0];
        this.geocodedCoordinates = {
          lat: parseFloat(firstMatch.lat),
          lng: parseFloat(firstMatch.lon),
          accuracy: firstMatch.accuracy_abbr || 'W',
          source: firstMatch.source_abbr || 'W'
        };
        this.enableConfirmButton();
      } else {
        console.warn('Radius Search: Geocoder service returned no matches');
        this.clearCoordinates();
        this.toastGeocoderFailed();
      }
    } catch (error) {
      console.error('Radius Search: Error calling geocoder service:', error);
      this.clearCoordinates();
      this.toastInvalidAddress();
    }
  }

  disableConfirmButton() {
    this.confirmButtonTarget.classList.add('disabled');
    this.confirmButtonTarget.disabled = true;
  }

  enableConfirmButton() {
    this.confirmButtonTarget.classList.remove('disabled');
    this.confirmButtonTarget.disabled = false;
  }

  confirmRadiusSearch(event) {
    event.preventDefault();
    
    if (!this.geocodedCoordinates) {
      console.error('Radius Search: No geocoded coordinates available');
      this.toastInvalidAddress();
      return;
    }

    if (!this.selectedAddressText) {
      this.clearCoordinates();
      this.disableConfirmButton();
      this.toastInvalidAddress();
      return;
    }

    const distanceInput = this.radiusInputTarget;
    const distance = parseFloat(distanceInput.value) || 1;

    if (distance <= 0 || distance > getMaxSearchRadius()) {
      // The max distance is a long number to display to the user, so we'll cap it at 2 decimal places
      toast.warning(`Distance must be greater than 0 and less than ${getMaxSearchRadius()} ${getDistanceUnit()}.`);
      return;
    }

    // Get current filter and add radius search
    const currentFilter = getState('filter');
    if (!currentFilter) {
      console.error('Radius Search: Filter state not available');
      toast.error('Unable to update search. Please try again.');
      return;
    }

    try {
      // Get the maps controller to access the map instance
      const mapContainer = document.getElementById('mapContainer');
      if (!mapContainer) {
        this.toastMapUnavailable();
        return;
      }

      const mapsController = this.application.getControllerForElementAndIdentifier(mapContainer, 'map--maps');
      if (!mapsController || !mapsController.map) {
        this.toastMapUnavailable();
        return;
      }

      // Create the circle using DrawingManager
      const circle = mapsController.map.drawingManager.createCircle(
        this.geocodedCoordinates.lat,
        this.geocodedCoordinates.lng,
        mapConverters.milesToMeters(distance)
      );

      // Add the circle to the map
      mapsController.map.drawingManager.add(circle);

      // Dispatching this event will trigger updates to the filter, state, annotations, and map
      window.dispatchEvent(mapEvents.drawingComplete(circle, { radiusSearch: true }));
      
      // Close modal
      this.closeModal();
    } catch (error) {
      console.error('Radius Search: Error adding radius search to filter:', error);
      toast.error('Unable to add Radius Search. Please try again.');
    }
  }

  closeModal() {
    $('#fastModal').modal('hide');
  }

  //////////////////
  // Toast messages
  //////////////////
  toastInvalidDistance(){
    toast.warning('Distance must be a number.');
  }

  toastRadiusSearchUnavailable(){
    toast.error('Radius Search is not available. Please refresh the page and try again.');
  }

  toastDistanceTooHigh(){
    toast.warning(`Distance cannot be greater than ${getMaxSearchRadius()} ${getDistanceUnit()}.`);
  }

  toastDistanceTooLow(){
    toast.warning(`Distance must be greater than 0 ${getDistanceUnit()}.`);
  }

  toastInvalidAddress(){
    toast.warning('Please select a valid address from the suggestions.');
  }

  toastGeocoderFailed(){
    toast.warning("Unable to verify this address. Please try another.");
  }

  toastMapUnavailable(){
    toast.error('The map is not available. Please refresh the page and try again.');
  }

}
